import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import {
  baytownStats,
  demographics,
  incomeTrend,
  povertyTrend,
  crimeTrend,
  educationScores,
} from "@/lib/proposalData";

const navy = "#0A192F";
const slate = "#475569";
const red = "#8B0000";
const gold = "#D4AF37";
const grid = "#CBD5E1";

function SectionLabel({ children }) {
  return (
    <div className="flex items-center gap-3 mb-3">
      <span className="h-px w-8 bg-[#8B0000]" />
      <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#8B0000]">
        {children}
      </span>
    </div>
  );
}

function ChartCard({ title, subtitle, children, testId, large = false, badge = "Verified" }) {
  const badgeColor = badge === "Verified" ? "text-[#0A192F]" : "text-[#8B0000]";
  return (
    <div
      data-testid={testId}
      className={`bg-white border border-[#CBD5E1] p-6 lg:p-7 ${large ? "lg:col-span-2" : ""}`}
    >
      <div className="flex items-start justify-between gap-4 mb-1">
        <h3 className="font-display text-lg text-[#0A192F]">{title}</h3>
        <span className={`font-mono text-[9px] uppercase tracking-widest ${badgeColor} mt-1.5 shrink-0`}>
          {badge}
        </span>
      </div>
      <p className="text-xs text-[#475569] mb-5">{subtitle}</p>
      <div className="h-56 lg:h-64">{children}</div>
    </div>
  );
}

export default function LocalContext() {
  const fmt = (n) => `$${(n / 1000).toFixed(1)}k`;
  return (
    <section
      id="context"
      data-testid="section-local-context"
      className="bg-[#F9F7F1] py-24 lg:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10 mb-14">
          <div className="lg:col-span-5">
            <SectionLabel>01 — The Local Context</SectionLabel>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-[#0A192F] leading-tight">
              The numbers behind a fragmented safety net.
            </h2>
          </div>
          <div className="lg:col-span-7 lg:pt-2">
            <p className="text-[#334155] text-base leading-relaxed">
              Baytown is a working-class, majority-minority community of{" "}
              <strong className="text-[#0A192F]">86,004 residents</strong> straddling
              Harris and Chambers counties. The median income is rising, but{" "}
              <strong className="text-[#0A192F]">nearly one in five residents</strong>{" "}
              still lives below the poverty line, and{" "}
              <strong className="text-[#0A192F]">77.6% of residents report rising
              housing costs</strong> over the past three years — a finding documented
              directly in the City's 2025–2029 Consolidated Plan.
            </p>
            <p className="mt-4 text-xs text-[#64748B] font-mono uppercase tracking-wider">
              Snapshot figures are verified from U.S. Census QuickFacts and the City of
              Baytown 2025–2029 Consolidated Plan. Multi-year trend lines are
              illustrative approximations from ACS 5-Year, TEA TAPR, and FBI UCR.
            </p>
          </div>
        </div>

        {/* Quick stat ribbon */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[#CBD5E1] border border-[#CBD5E1] mb-12">
          {[
            { k: `${baytownStats.poverty_rate_pct}%`, v: "Overall Poverty", accent: red },
            { k: `${baytownStats.hispanic_latino_pct}%`, v: "Hispanic / Latino", accent: navy },
            { k: `${baytownStats.rising_housing_costs_pct}%`, v: "Rising Housing Costs (3-yr)", accent: red },
            { k: `${baytownStats.seniors_65_plus_pct}%`, v: "Seniors 65+", accent: navy },
          ].map((s) => (
            <div
              key={s.v}
              data-testid={`context-ribbon-${s.v.toLowerCase().replace(/\s/g, "-")}`}
              className="bg-white px-6 py-7"
            >
              <div className="font-display text-3xl" style={{ color: s.accent }}>
                {s.k}
              </div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#475569] mt-2">
                {s.v}
              </div>
            </div>
          ))}
        </div>

        {/* Charts grid */}
        <div className="grid lg:grid-cols-2 gap-6">
          <ChartCard
            testId="chart-income-trend"
            title="Median Household Income — Baytown"
            subtitle="$59,910 (2024 Census). Trend 2015–2023 illustrative; persistently below the Harris County metro average."
            large
            badge="Trend · Illustrative"
          >
            <ResponsiveContainer>
              <AreaChart data={incomeTrend} margin={{ top: 8, right: 24, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="gIncome" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={navy} stopOpacity={0.35} />
                    <stop offset="100%" stopColor={navy} stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke={grid} strokeDasharray="2 4" vertical={false} />
                <XAxis dataKey="year" stroke={slate} tickLine={false} axisLine={{ stroke: grid }} />
                <YAxis
                  stroke={slate}
                  tickFormatter={fmt}
                  tickLine={false}
                  axisLine={{ stroke: grid }}
                />
                <Tooltip
                  formatter={(v) => [`$${v.toLocaleString()}`, "Median Income"]}
                  cursor={{ stroke: red, strokeWidth: 1 }}
                />
                <Area
                  type="monotone"
                  dataKey="median_income"
                  stroke={navy}
                  strokeWidth={2}
                  fill="url(#gIncome)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard
            testId="chart-poverty-trend"
            title="Poverty Rates — Overall vs. Children"
            subtitle="Overall poverty 17.7% (Census Verified). Trend approximated from ACS 5-Year."
            badge="Mixed"
          >
            <ResponsiveContainer>
              <LineChart data={povertyTrend} margin={{ top: 8, right: 24, left: 0, bottom: 0 }}>
                <CartesianGrid stroke={grid} strokeDasharray="2 4" vertical={false} />
                <XAxis dataKey="year" stroke={slate} tickLine={false} axisLine={{ stroke: grid }} />
                <YAxis
                  stroke={slate}
                  tickFormatter={(v) => `${v}%`}
                  tickLine={false}
                  axisLine={{ stroke: grid }}
                  domain={[12, 28]}
                />
                <Tooltip formatter={(v) => `${v}%`} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line type="monotone" dataKey="poverty" name="Overall" stroke={navy} strokeWidth={2} dot={{ r: 3 }} />
                <Line
                  type="monotone"
                  dataKey="child_poverty"
                  name="Children under 18"
                  stroke={red}
                  strokeWidth={2}
                  dot={{ r: 3 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard
            testId="chart-demographics"
            title="Demographic Composition"
            subtitle="Verified from U.S. Census QuickFacts 2024. Baytown is a majority-minority community."
            badge="Verified"
          >
            <ResponsiveContainer>
              <PieChart>
                <Pie
                  data={demographics}
                  dataKey="pct"
                  nameKey="group"
                  innerRadius="48%"
                  outerRadius="78%"
                  paddingAngle={2}
                  stroke="#F9F7F1"
                  strokeWidth={2}
                >
                  {demographics.map((d, i) => (
                    <Cell key={i} fill={d.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(v) => `${v}%`} />
                <Legend
                  layout="vertical"
                  verticalAlign="middle"
                  align="right"
                  wrapperStyle={{ fontSize: 11 }}
                />
              </PieChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard
            testId="chart-crime-trend"
            title="Crime per 1,000 Residents"
            subtitle="Both property and violent crime have trended downward since 2018."
            badge="Trend · Illustrative"
          >
            <ResponsiveContainer>
              <LineChart data={crimeTrend} margin={{ top: 8, right: 24, left: 0, bottom: 0 }}>
                <CartesianGrid stroke={grid} strokeDasharray="2 4" vertical={false} />
                <XAxis dataKey="year" stroke={slate} tickLine={false} axisLine={{ stroke: grid }} />
                <YAxis
                  stroke={slate}
                  tickLine={false}
                  axisLine={{ stroke: grid }}
                  tickFormatter={(v) => `${v}`}
                />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line type="monotone" dataKey="property" name="Property" stroke={navy} strokeWidth={2} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="violent" name="Violent" stroke={red} strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard
            testId="chart-education-scores"
            title="GCCISD vs. Texas State (Approaches Grade Level)"
            subtitle="Local students trail state averages on reading, math, and science. Source: TEA TAPR."
            large
            badge="TEA TAPR"
          >
            <ResponsiveContainer>
              <BarChart data={educationScores} margin={{ top: 8, right: 24, left: 0, bottom: 0 }}>
                <CartesianGrid stroke={grid} strokeDasharray="2 4" vertical={false} />
                <XAxis dataKey="subject" stroke={slate} tickLine={false} axisLine={{ stroke: grid }} />
                <YAxis
                  stroke={slate}
                  tickFormatter={(v) => `${v}%`}
                  tickLine={false}
                  axisLine={{ stroke: grid }}
                  domain={[0, 100]}
                />
                <Tooltip formatter={(v) => `${v}%`} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="gccisd" name="GCCISD" fill={red} radius={[2, 2, 0, 0]} />
                <Bar dataKey="texas" name="Texas State" fill={navy} radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>
      </div>
    </section>
  );
}
